/**
 * DÖNEMSEL ANALİZ TERCİHLERİ — son seçim hatırlanır (kullanıcı, 13.10.2026).
 *
 * NEREDE: profilin ŞİFRELİ kabında (`repo.settings`), tarayıcının düz
 * deposunda DEĞİL. Seçilen varlıklar ve sepet oranları bir tür portföy
 * bilgisidir; grafik tercihleri gibi `localStorage`a yazılmaz.
 *
 * Bozuk ya da eski kayıt sessizce varsayılana düşer; bilinmeyen alan atılır.
 */

import {
  DONEMSEL_MAX_ARAC,
  DONEMSEL_MAX_ORTALAMA_GUN,
  DONEMSEL_MAX_PERIYOT_GUN,
  DONEMSEL_MIN_ORTALAMA_GUN,
  SEPET_DENGELEMELERI,
} from "@parafon/contracts/local-api";
import type { SepetDengeleme } from "@parafon/contracts/local-api";
import type { IsoDate } from "@parafon/contracts";
import { isKnownCurrency } from "@parafon/contracts";
import { DONEMLER, donemAraligi, type DonemKodu } from "./tarihDonemleri";
import { M } from "../dil";

export const DONEMSEL_TERCIH_ANAHTARI = "donemselAnaliz.tercih";

/**
 * Hazır periyotlar (kullanıcı kararı 13.10.2026; 2–5 yıl kullanıcı isteği
 * 29.10.2026) + serbest gün sayısı. Yıl = 365 gün (1 yılla aynı kural); en
 * uzunu `DONEMSEL_MAX_PERIYOT_GUN` içinde.
 */
export const PERIYOTLAR: readonly { readonly gun: number; readonly ad: string }[] = [
  { gun: 7, ad: M.donemsel.periyot.g7 },
  { gun: 30, ad: M.donemsel.periyot.g30 },
  { gun: 92, ad: M.donemsel.periyot.g92 },
  { gun: 182, ad: M.donemsel.periyot.g182 },
  { gun: 365, ad: M.donemsel.periyot.g365 },
  { gun: 730, ad: M.donemsel.periyot.g730 },
  { gun: 1095, ad: M.donemsel.periyot.g1095 },
  { gun: 1460, ad: M.donemsel.periyot.g1460 },
  { gun: 1825, ad: M.donemsel.periyot.g1825 },
];

export interface DonemselTercih {
  readonly secili: readonly string[];
  readonly donem: DonemKodu | null;
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly para: string;
  readonly periyotGun: number;
  readonly temettu: boolean;
  /** Ortalama fiyat: son N işlem günü (kullanıcı kararı 13.10.2026). */
  readonly ortalamaAcik: boolean;
  readonly ortalamaGun: number;
  readonly sepetAcik: boolean;
  /** Araç → yüzde metni ("40", "33,33"). */
  readonly agirliklar: Readonly<Record<string, string>>;
  readonly dengeleme: SepetDengeleme;
  readonly yalnizSepet: boolean;
  /** Varlık listesi açık mı. */
  readonly listeAcik: boolean;
}

export function varsayilanDonemselTercih(bugun: IsoDate): DonemselTercih {
  return {
    secili: [],
    donem: "son-3-yil",
    ...donemAraligi("son-3-yil", bugun),
    para: "USD",
    periyotGun: 92,
    temettu: false,
    ortalamaAcik: true,
    ortalamaGun: 7,
    sepetAcik: false,
    agirliklar: {},
    dengeleme: "yok",
    yalnizSepet: false,
    listeAcik: true,
  };
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function donemselTercihOku(metin: string | null, bugun: IsoDate): DonemselTercih {
  const v = varsayilanDonemselTercih(bugun);
  if (metin === null) return v;
  let ham: unknown;
  try {
    ham = JSON.parse(metin);
  } catch {
    return v;
  }
  if (typeof ham !== "object" || ham === null || Array.isArray(ham)) return v;
  const o = ham as Record<string, unknown>;

  const secili = Array.isArray(o["secili"])
    ? [...new Set(o["secili"].filter((x): x is string => typeof x === "string" && x !== ""))].slice(0, DONEMSEL_MAX_ARAC)
    : v.secili;

  let aralik: { donem: DonemKodu | null; from: IsoDate; to: IsoDate } = { donem: v.donem, from: v.from, to: v.to };
  const kod = DONEMLER.find((d) => d.kod === o["donem"])?.kod;
  if (kod !== undefined) aralik = { donem: kod, ...donemAraligi(kod, bugun) };
  else if (
    o["donem"] === null &&
    typeof o["from"] === "string" &&
    typeof o["to"] === "string" &&
    ISO.test(o["from"]) &&
    ISO.test(o["to"])
  ) {
    aralik = { donem: null, from: o["from"], to: o["to"] };
  }

  const para = typeof o["para"] === "string" && isKnownCurrency(o["para"]) ? o["para"] : v.para;
  const p = o["periyotGun"];
  const periyotGun =
    typeof p === "number" && Number.isInteger(p) && p >= 1 && p <= DONEMSEL_MAX_PERIYOT_GUN ? p : v.periyotGun;

  const og = o["ortalamaGun"];
  const ortalamaGun =
    typeof og === "number" && Number.isInteger(og) && og >= DONEMSEL_MIN_ORTALAMA_GUN && og <= DONEMSEL_MAX_ORTALAMA_GUN
      ? og
      : v.ortalamaGun;

  const agirliklar: Record<string, string> = {};
  const a = o["agirliklar"];
  if (typeof a === "object" && a !== null && !Array.isArray(a)) {
    for (const [k, val] of Object.entries(a)) if (typeof val === "string") agirliklar[k] = val;
  }
  const d = o["dengeleme"];
  const dengeleme = (SEPET_DENGELEMELERI as readonly unknown[]).includes(d) ? (d as SepetDengeleme) : v.dengeleme;

  return {
    secili,
    ...aralik,
    para,
    periyotGun,
    temettu: o["temettu"] === true,
    ortalamaAcik: o["ortalamaAcik"] !== false,
    ortalamaGun,
    sepetAcik: o["sepetAcik"] === true,
    agirliklar,
    dengeleme,
    yalnizSepet: o["yalnizSepet"] === true,
    listeAcik: o["listeAcik"] !== false,
  };
}

export function donemselTercihYaz(t: DonemselTercih): string {
  return JSON.stringify(t);
}
